import React, { useState, useRef, useEffect } from "react";

import { Modal, Spinner } from "react-bootstrap";
import { RxCross1 } from "react-icons/rx";
import { MdErrorOutline } from "react-icons/md";

import html2canvas from "html2canvas";

import { Button, ConfirmDialog, NavigationButton } from "./decorate";

import { STEPS_COMPONENT } from "../constants";

import { getLogoDimentions, debounce } from "../helpers";

import { uploadBaseImage } from "../actions";

import {
  useEditor,
  useError,
  useGallery,
  useLoader,
  useShirtArea,
  useStep,
} from "../hooks";

// import BASE_URL from "../config";

const textureSrc =
  "https://media.istockphoto.com/id/1325383252/vector/jeans-background-denim-seamless-pattern-jeans-fabric-linen-canvas-fabric-texture-vector.jpg?s=1024x1024&w=is&k=20&c=TQRu3fjAEkPP0Sy5dv8Slu1D1fXbAkENZfKStv0Ex1I=";

// const textureSrc = `${BASE_URL}media/iStock-1325383252.jpg`;

const CustomizationModal = ({
  show = false,
  isSaved = false,
  customizationDetails = {},
  currentPart = {},
  urls = {},
  partsStep = {},
  partsUploadPreference = {},
  selected = null,
  notes = "",

  setUrls = () => {},
  setShow = () => {},
  setNotes = () => {},
  setSelected = () => {},
  setPartsStep = () => {},
  onLabelChange = () => {},
  setCurrentPart = () => {},
  resetButtonLabel = () => {},
  setCustomizationDetails = () => {},
  setPartsUploadPreference = () => {},
}) => {
  const canvasRef = useRef(null);
  const textureCanvasRef = useRef(null);
  const normalCanvasRef = useRef(null);
  const textureImgRef = useRef(null);
  const shirtImgRef = useRef(null);

  const { step, setStep, deviceStep, setDeviceStep } = useStep();
  const { isLoading, onLoader, offLoader } = useLoader();
  const { activeFormats } = useEditor();
  const { setError, clearError } = useError();
  const { recentUploads, getRecentUploads } = useGallery();
  const { areas, isWrongFormat } = useShirtArea();

  const [zoom, setZoom] = useState({});
  const [zoomValue, setZoomValue] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);

  const isLastStep = 3 + Object.keys(partsStep || {})?.length + 1 === step;

  const onAccept = () => {
    setOpenConfirm(false);
    setShow(false);
    setStep(1);
    setDeviceStep(0);
    setPartsStep(null);
    setPartsUploadPreference(null);
    setSelected(null);
    setZoom({});
    setCurrentPart({});
    setZoomValue(null);
    clearError();
    setUrls({
      logo_url: null,
      cropped_url: null,
      bgRemovedUrl: null,
      showedBgRemovedUrl: null,
      texture_logo_url: null,
    });
    setCustomizationDetails({
      termsAccepted: false,
      isEmbroidery: null,
      chest: null,
      sleeve: null,
      back: null,
      isBgRemoved: null,
    });
    setNotes("");
  };
  const onReject = () => setOpenConfirm(false);

  //<-------step handlers------------>
  const backStep = () => {
    if (step !== 1) {
      setStep((prevstep) => {
        return prevstep - 1;
      });
    } else onClose();
  };

  const nextPhaseHandler = () => {
    if (isWrongFormat) return;
    const { termsAccepted, chest, back, sleeve, isEmbroidery } =
      customizationDetails;

    let errorObj = {};
    let valid = true;

    if (step === 1) {
      if (termsAccepted === false) {
        errorObj = { terms: null, isLabeled: true };
        valid = false;
        setError({ ...errorObj });
        return;
      }
      if (isEmbroidery === null) {
        errorObj = { type: "Select a type.", isLabeled: false };
        valid = false;
      }
    }
    if (step === 2) {
      if (!chest && !back && !sleeve) {
        errorObj = {
          placement: "Select atleast one location.",
          isLabeled: false,
        };
        valid = false;
      }
    }

    if (step === 3 && selected === "text" && !activeFormats?.common?.text) {
      errorObj = {
        editor: "Add text to continue.",
        isLabeled: false,
      };
      valid = false;
    }

    if (!valid) {
      setError({ ...errorObj });
      return;
    } else {
      clearError();
    }
    if (!isLastStep) {
      setStep((prevStep) => {
        if (prevStep >= 4 && prevStep <= 6 && partsStep[`step${prevStep}`])
          savePreviewImage(prevStep, prevStep + 1);

        return prevStep + 1;
      });
    } else if (isSaved) {
      onClose();
      localStorage.removeItem("customization_payload");
      resetButtonLabel();
    } else {
      onCart();
    }

    clearError();
  };

  //<-------url handler------------>
  const handleUrls = (data = {}) => {
    setUrls((prevstate) => ({
      ...prevstate,
      ...data,
    }));
  };

  //<------customization details handler------->
  const handleCustomizationDetails = ({ value, name, checked }) => {
    setCustomizationDetails((prevDetails) => ({
      ...prevDetails,
      [name]: name === "termsAccepted" ? checked : value,
    }));
  };

  //<------handle logo poistions selection parts------>
  const setLogoPositions = (name, key) => {
    const isRemoving = customizationDetails[name]?.key === key;

    if (isRemoving) {
      const newCustomizationDetails = { ...customizationDetails };
      delete newCustomizationDetails[name];
      buildStepsAfterUpdate(newCustomizationDetails);
      return;
    }

    const data = {
      ...customizationDetails,
      [name]: { key, ...areas?.[key] },
    };

    buildStepsAfterUpdate(data);
  };

  const buildStepsAfterUpdate = (data) => {
    // Build an array of parts in order of priority:
    // 1. chest, 2. sleeve, 3. back.
    const steps = [];

    // Add chest first if available
    if (data.chest) {
      steps.push({ ...data.chest, name: "chest" });
    }

    // Add sleeve if it exists and has a different key than the last added part
    if (data.sleeve && data.sleeve.key !== steps[steps.length - 1]?.key) {
      steps.push({ ...data.sleeve, name: "sleeve" });
    }

    // Add back if it exists and has a different key than the last added part
    if (data.back && data.back.key !== steps[steps.length - 1]?.key) {
      steps.push({ ...data.back, name: "back" });
    }

    // Map the collected parts to our expected step keys
    const partsStep = {};
    if (steps[0]) partsStep.step4 = { ...steps[0], header: "Location 1" };
    if (steps[1]) partsStep.step5 = { ...steps[1], header: "Location 2" };
    if (steps[2]) partsStep.step6 = { ...steps[2], header: "Location 3" };

    // Update state
    setPartsStep(partsStep);
    setCustomizationDetails(data);
  };

  //<-----remove background and texture proccessing handlers-------->
  // const removeBackground = (url, cb = null) => {
  //   const canvas = canvasRef.current;
  //   const ctx = canvas?.getContext("2d");
  //   if (!ctx) return;
  //   onLoader();
  //   const img = new Image();
  //   img.crossOrigin = "Anonymous"; // Avoid CORS issues
  //   img.src = url;

  //   offLoader();

  //   img.onload = () => {
  //     // Set canvas dimensions to the image dimensions and draw the image.
  //     canvas.width = img.width;
  //     canvas.height = img.height;
  //     ctx.drawImage(img, 0, 0);

  //     // Get the image data from the canvas.
  //     let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //     let data = imageData.data;

  //     // Check if the image is fully opaque. (Most non-background-removed images are completely opaque.)
  //     let isFullyOpaque = true;
  //     for (let i = 0; i < data.length; i += 4) {
  //       if (data[i + 3] !== 255) {
  //         isFullyOpaque = false;
  //         break;
  //       }
  //     }

  //     // If the image is opaque, remove the background.
  //     if (isFullyOpaque) {
  //       // Use the first pixel as a sample background color.
  //       const bgColor = { r: data[0], g: data[1], b: data[2] };

  //       // Replace pixels similar to the background color with white.
  //       for (let i = 0; i < data.length; i += 4) {
  //         const r = data[i],
  //           g = data[i + 1],
  //           b = data[i + 2];
  //         if (
  //           Math.abs(r - bgColor.r) < 40 &&
  //           Math.abs(g - bgColor.g) < 40 &&
  //           Math.abs(b - bgColor.b) < 40
  //         ) {
  //           data[i] = 255;
  //           data[i + 1] = 255;
  //           data[i + 2] = 255;
  //         }
  //       }

  //       // Convert the white areas (background) to transparent.
  //       for (let i = 0; i < data.length; i += 4) {
  //         if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) {
  //           data[i + 3] = 0;
  //         }
  //       }

  //       // Write the processed data back to the canvas.
  //       ctx.putImageData(imageData, 0, 0);
  //     }
  //     // If not fully opaque, we assume the background is already removed.

  //     // Save the (possibly processed) image as a PNG data URL.
  //     const transparentDataURL = canvas.toDataURL("image/webp");
  //     // ----------------- Crop the Image -----------------
  //     // Re-read the image data (in case background removal was applied)
  //     const newImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //     const newData = newImageData.data;

  //     // Determine the bounding box for all non-transparent pixels.
  //     let minX = canvas.width,
  //       minY = canvas.height,
  //       maxX = 0,
  //       maxY = 0;
  //     let foundPixel = false;

  //     for (let y = 0; y < canvas.height; y++) {
  //       for (let x = 0; x < canvas.width; x++) {
  //         const index = (y * canvas.width + x) * 4;
  //         if (newData[index + 3] > 0) {
  //           if (x < minX) minX = x;
  //           if (x > maxX) maxX = x;
  //           if (y < minY) minY = y;
  //           if (y > maxY) maxY = y;
  //           foundPixel = true;
  //         }
  //       }
  //     }

  //     let cropWidth = canvas.width;
  //     let cropHeight = canvas.height;

  //     let croppedDataURL = transparentDataURL;

  //     if (foundPixel) {
  //       cropWidth = maxX - minX + 1;
  //       cropHeight = maxY - minY + 1;
  //       // Create a new canvas for the cropped image
  //       const croppedCanvas = document.createElement("canvas");
  //       const croppedCtx = croppedCanvas.getContext("2d");
  //       croppedCanvas.width = cropWidth;
  //       croppedCanvas.height = cropHeight;
  //       // Draw the cropped area from the original canvas
  //       croppedCtx.drawImage(
  //         canvas,
  //         minX,
  //         minY,
  //         cropWidth,
  //         cropHeight,
  //         0,
  //         0,
  //         cropWidth,
  //         cropHeight
  //       );

  //       croppedDataURL = croppedCanvas.toDataURL("image/png");
  //     }

  //     if (cb) {
  //       cb(transparentDataURL, croppedDataURL);
  //     } else {
  //       handleUrls({
  //         showedBgRemovedUrl: transparentDataURL,
  //         bgRemovedUrl: croppedDataURL,
  //       });

  //       Object.values(partsStep).forEach((part) => {
  //         part.showedBgRemovedUrl = transparentDataURL;
  //         part.bgRemovedUrl = croppedDataURL;
  //         part.cropped_url = urls?.cropped_url;
  //         part.zoomValue = part.logo_max_width;
  //       });

  //       getTexture(croppedDataURL, null, true);
  //     }
  //   };
  // };

  const removeBackground = (url, cb = null) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    onLoader();

    const img = new Image();
    img.crossOrigin = "Anonymous"; // Avoid CORS issues
    img.src = url;

    img.onload = () => {
      offLoader();

      // Set canvas dimensions to the image dimensions and draw the image.
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // Get the image data from the canvas.
      let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let data = imageData.data;

      // Check if the image is fully opaque. (Most non-background-removed images are completely opaque.)
      let isFullyOpaque = true;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] !== 255) {
          isFullyOpaque = false;
          break;
        }
      }

      // If the image is opaque, remove the background.
      if (isFullyOpaque) {
        // Sample background color from slightly inside the top-left corner (5% inward)
        const offsetX = Math.max(1, Math.floor(canvas.width * 0.05));
        const offsetY = Math.max(1, Math.floor(canvas.height * 0.05));
        const cornerIndex = (offsetY * canvas.width + offsetX) * 4;

        const bgColor = {
          r: data[cornerIndex],
          g: data[cornerIndex + 1],
          b: data[cornerIndex + 2],
        };

        // Create a map to track which pixels are similar to the background color
        const bgPixels = new Set();

        // First pass: identify pixels similar to the background color
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2];
          if (
            Math.abs(r - bgColor.r) < 40 &&
            Math.abs(g - bgColor.g) < 40 &&
            Math.abs(b - bgColor.b) < 40
          ) {
            bgPixels.add(i);
          }
        }

        // Check if white pixels exist in the original image (that are not background)
        let hasWhiteContent = false;
        for (let i = 0; i < data.length; i += 4) {
          // If the pixel is white or nearly white and NOT identified as background
          if (
            data[i] > 240 &&
            data[i + 1] > 240 &&
            data[i + 2] > 240 &&
            !bgPixels.has(i)
          ) {
            hasWhiteContent = true;
            break;
          }
        }

        // Apply background removal based on the bgPixels set
        for (let i = 0; i < data.length; i += 4) {
          if (bgPixels.has(i)) {
            // Set background pixels to transparent white
            data[i] = 255; // R
            data[i + 1] = 255; // G
            data[i + 2] = 255; // B
            data[i + 3] = 0; // Alpha (transparent)
          }
        }

        // Convert background color to transparent only if there are no white content pixels
        if (!hasWhiteContent) {
          for (let i = 0; i < data.length; i += 4) {
            if (
              data[i] > 240 &&
              data[i + 1] > 240 &&
              data[i + 2] > 240 &&
              data[i + 3] === 255
            ) {
              data[i + 3] = 0; // Make white pixels transparent
            }
          }
        }

        // Write the processed data back to the canvas.
        ctx.putImageData(imageData, 0, 0);
      }
      // If not fully opaque, we assume the background is already removed.

      // Save the (possibly processed) image as a PNG data URL.
      const transparentDataURL = canvas.toDataURL("image/webp");

      // ----------------- Crop the Image -----------------
      // Re-read the image data (in case background removal was applied)
      const newImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const newData = newImageData.data;

      // Determine the bounding box for all non-transparent pixels.
      let minX = canvas.width,
        minY = canvas.height,
        maxX = 0,
        maxY = 0;
      let foundPixel = false;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4;
          if (newData[index + 3] > 0) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
            foundPixel = true;
          }
        }
      }

      let cropWidth = canvas.width;
      let cropHeight = canvas.height;

      let croppedDataURL = transparentDataURL;

      if (foundPixel) {
        cropWidth = maxX - minX + 1;
        cropHeight = maxY - minY + 1;
        // Create a new canvas for the cropped image
        const croppedCanvas = document.createElement("canvas");
        const croppedCtx = croppedCanvas.getContext("2d");
        croppedCanvas.width = cropWidth;
        croppedCanvas.height = cropHeight;
        // Draw the cropped area from the original canvas
        croppedCtx.drawImage(
          canvas,
          minX,
          minY,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight,
        );

        croppedDataURL = croppedCanvas.toDataURL("image/png");
      }

      if (cb) {
        cb(transparentDataURL, croppedDataURL);
      } else {
        handleUrls({
          showedBgRemovedUrl: transparentDataURL,
          bgRemovedUrl: croppedDataURL,
        });
        const partsData = Object.values(partsStep) || [];
        partsData.forEach((part) => {
          part.showedBgRemovedUrl = transparentDataURL;
          part.bgRemovedUrl = croppedDataURL;
          part.cropped_url = urls?.cropped_url;
          part.zoomValue = 100;
          part.temp_url = urls?.cropped_url;
        });

        setZoomValue(100);

        getTexture(croppedDataURL, null, true);
      }
    };
  };

  // const removeBackground = (url, cb = null) => {
  //   const canvas = canvasRef.current;
  //   const ctx = canvas?.getContext("2d");
  //   if (!ctx) return;
  //   onLoader();
  //   const img = new Image();
  //   img.crossOrigin = "Anonymous"; // Avoid CORS issues
  //   img.src = url;

  //   img.onload = () => {
  //     offLoader();

  //     // Set canvas dimensions to the image dimensions and draw the image.
  //     canvas.width = img.width;
  //     canvas.height = img.height;
  //     ctx.drawImage(img, 0, 0);

  //     // Get the image data from the canvas.
  //     let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //     let data = imageData.data;

  //     // Check if the image is fully opaque. (Most non-background-removed images are completely opaque.)
  //     let isFullyOpaque = true;
  //     for (let i = 0; i < data.length; i += 4) {
  //       if (data[i + 3] !== 255) {
  //         isFullyOpaque = false;
  //         break;
  //       }
  //     }

  //     // If the image is opaque, remove the background.
  //     if (isFullyOpaque) {
  //       // Use the first pixel as a sample background color.
  //       const bgColor = { r: data[0], g: data[1], b: data[2] };

  //       // Create a map to track which pixels are similar to the background color
  //       const bgPixels = new Set();

  //       // First pass: identify pixels similar to the background color
  //       for (let i = 0; i < data.length; i += 4) {
  //         const r = data[i],
  //           g = data[i + 1],
  //           b = data[i + 2];
  //         if (
  //           Math.abs(r - bgColor.r) < 40 &&
  //           Math.abs(g - bgColor.g) < 40 &&
  //           Math.abs(b - bgColor.b) < 40
  //         ) {
  //           bgPixels.add(i);
  //         }
  //       }

  //       // Check if white pixels exist in the original image (that are not background)
  //       let hasWhiteContent = false;
  //       for (let i = 0; i < data.length; i += 4) {
  //         // If the pixel is white or nearly white and NOT identified as background
  //         if (
  //           data[i] > 240 &&
  //           data[i + 1] > 240 &&
  //           data[i + 2] > 240 &&
  //           !bgPixels.has(i)
  //         ) {
  //           hasWhiteContent = true;
  //           break;
  //         }
  //       }

  //       // Apply background removal based on the bgPixels set
  //       for (let i = 0; i < data.length; i += 4) {
  //         if (bgPixels.has(i)) {
  //           // Set background pixels to transparent white
  //           data[i] = 255; // R
  //           data[i + 1] = 255; // G
  //           data[i + 2] = 255; // B
  //           data[i + 3] = 0; // Alpha (transparent)
  //         }
  //       }

  //       // Convert background color to transparent only if there are no white content pixels
  //       if (!hasWhiteContent) {
  //         for (let i = 0; i < data.length; i += 4) {
  //           if (
  //             data[i] > 240 &&
  //             data[i + 1] > 240 &&
  //             data[i + 2] > 240 &&
  //             data[i + 3] === 255
  //           ) {
  //             data[i + 3] = 0; // Make white pixels transparent
  //           }
  //         }
  //       }

  //       // Write the processed data back to the canvas.
  //       ctx.putImageData(imageData, 0, 0);
  //     }
  //     // If not fully opaque, we assume the background is already removed.

  //     // Save the (possibly processed) image as a PNG data URL.
  //     const transparentDataURL = canvas.toDataURL("image/webp");

  //     // ----------------- Crop the Image -----------------
  //     // Re-read the image data (in case background removal was applied)
  //     const newImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //     const newData = newImageData.data;

  //     // Determine the bounding box for all non-transparent pixels.
  //     let minX = canvas.width,
  //       minY = canvas.height,
  //       maxX = 0,
  //       maxY = 0;
  //     let foundPixel = false;

  //     for (let y = 0; y < canvas.height; y++) {
  //       for (let x = 0; x < canvas.width; x++) {
  //         const index = (y * canvas.width + x) * 4;
  //         if (newData[index + 3] > 0) {
  //           if (x < minX) minX = x;
  //           if (x > maxX) maxX = x;
  //           if (y < minY) minY = y;
  //           if (y > maxY) maxY = y;
  //           foundPixel = true;
  //         }
  //       }
  //     }

  //     let cropWidth = canvas.width;
  //     let cropHeight = canvas.height;

  //     let croppedDataURL = transparentDataURL;

  //     if (foundPixel) {
  //       cropWidth = maxX - minX + 1;
  //       cropHeight = maxY - minY + 1;
  //       // Create a new canvas for the cropped image
  //       const croppedCanvas = document.createElement("canvas");
  //       const croppedCtx = croppedCanvas.getContext("2d");
  //       croppedCanvas.width = cropWidth;
  //       croppedCanvas.height = cropHeight;
  //       // Draw the cropped area from the original canvas
  //       croppedCtx.drawImage(
  //         canvas,
  //         minX,
  //         minY,
  //         cropWidth,
  //         cropHeight,
  //         0,
  //         0,
  //         cropWidth,
  //         cropHeight
  //       );

  //       croppedDataURL = croppedCanvas.toDataURL("image/png");
  //     }

  //     if (cb) {
  //       cb(transparentDataURL, croppedDataURL);
  //     } else {
  //       handleUrls({
  //         showedBgRemovedUrl: transparentDataURL,
  //         bgRemovedUrl: croppedDataURL,
  //       });
  //       const partsData = Object.values(partsStep) || [];
  //       partsData.forEach((part) => {
  //         part.showedBgRemovedUrl = transparentDataURL;
  //         part.bgRemovedUrl = croppedDataURL;
  //         part.cropped_url = urls?.cropped_url;
  //         part.zoomValue = part.logo_max_width;
  //       });

  //       setZoomValue(partsData?.[0]?.logo_max_width);

  //       getTexture(croppedDataURL, null, true);
  //     }
  //   };
  // };

  const setDimentions = (
    url = "",
    cb = () => {},
    isBgRemoved,
    mainImage = "",
  ) => {
    let newMainImage = "";
    (async () => {
      newMainImage = mainImage;
    })();

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;

    img.onload = () => {
      if (cb) {
        const dimension = getLogoDimentions(
          img.height,
          img.width,
          currentPart?.box_dimension,
        );
        cb(url, dimension, newMainImage);
      } else {
        Object.values(partsStep).forEach((part) => {
          const dimension = getLogoDimentions(
            img.height,
            img.width,
            part?.box_dimension,
          );

          Object.assign(part, {
            logo_dimention: dimension,
            temp_dimention: dimension,
            text_url: newMainImage,
            texture_logo_url: url,
            isBgRemoved: isBgRemoved,
            type: selected === "gallery" ? "logo" : "text",
          });
        });

        handleUrls({ texture_logo_url: url });
      }
    };
  };

  const createCanvas = (width = 0, height = 0) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
  };

  // Function to generate a normal map from image data
  const generateNormalMap = (imageData, width, height) => {
    const data = imageData.data;
    const normalData = new ImageData(width, height);
    const strength = 1;

    const getGray = (x, y) => {
      x = Math.max(0, Math.min(x, width - 1));
      y = Math.max(0, Math.min(y, height - 1));
      const idx = (y * width + x) * 4;
      return 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const left = getGray(x - 1, y);
        const right = getGray(x + 1, y);
        const top = getGray(x, y - 1);
        const bottom = getGray(x, y + 1);

        const dx = (right - left) * strength;
        const dy = (bottom - top) * strength;

        let nx = -dx;
        let ny = -dy;
        let nz = 1;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        nx /= len;
        ny /= len;
        nz /= len;

        const idxOut = (y * width + x) * 4;
        normalData.data[idxOut + 0] = Math.floor((nx * 0.5 + 0.5) * 255);
        normalData.data[idxOut + 1] = Math.floor((ny * 0.5 + 0.5) * 255);
        normalData.data[idxOut + 2] = Math.floor((nz * 0.5 + 0.5) * 255);
        normalData.data[idxOut + 3] = 255;
      }
    }
    return normalData;
  };

  // Process the composite image using the uploaded image and the preloaded texture image
  const processComposite = (
    uploadedImage,
    textureImg,
    cb = null,
    isBgRemoved,
    mainImage = "",
  ) => {
    const width = uploadedImage.naturalWidth;
    const height = uploadedImage.naturalHeight;

    // Create a texture canvas and draw the preloaded texture image
    const textureCanvas = createCanvas(width, height);
    const textureCtx = textureCanvas.getContext("2d");

    if (!textureImg) {
      console.error("Texture image not loaded.");
      return;
    }
    textureCtx.clearRect(0, 0, width, height);
    textureCtx.drawImage(textureImg, 0, 0, width, height);

    // Generate the normal map based on the texture image
    const textureData = textureCtx.getImageData(0, 0, width, height);
    const normalMapData = generateNormalMap(textureData, width, height);

    // Create a normal canvas to hold the computed normal map
    const normalCanvas = createCanvas(width, height);
    const normalCtx = normalCanvas.getContext("2d");
    normalCtx.putImageData(normalMapData, 0, 0);

    // Set up the composite canvas for final output
    const compositeCanvas = createCanvas(width, height);
    const compCtx = compositeCanvas.getContext("2d");

    // Offscreen canvas to capture raw pixel data of the uploaded image
    const offCanvas = createCanvas(width, height);
    const offCtx = offCanvas.getContext("2d");
    offCtx.drawImage(uploadedImage, 0, 0, width, height);
    const uploadedData = offCtx.getImageData(0, 0, width, height);

    // Retrieve normal map pixel data
    const normalData = normalCtx.getImageData(0, 0, width, height);

    // Create new composite image data
    const compositeData = compCtx.createImageData(width, height);

    // Simple Lambert shading and bump mapping parameters
    const displacementStrength = 0;
    const blackThreshold = 30;
    const darkGrey = 80;
    const isBlackPixel = new Uint8Array(width * height);

    // Iterate over each pixel for shading calculations
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const pixelIndex = y * width + x;

        // Retrieve normal vector from the normal map
        const nR = normalData.data[idx];
        const nG = normalData.data[idx + 1];
        const nB = normalData.data[idx + 2];
        const nx = (nR / 255 - 0.5) * 2;
        const ny = (nG / 255 - 0.5) * 2;
        const nz = (nB / 255 - 0.5) * 2;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
        const normalX = nx / len;
        const normalY = ny / len;
        const normalZ = nz / len;

        // Define directional light parameters
        let lightX = 0,
          lightY = 0.8,
          lightZ = 0.5;

        const lightLen = Math.sqrt(
          lightX * lightX + lightY * lightY + (lightZ * lightZ) / 10,
        );
        lightX /= lightLen;
        lightY /= lightLen;
        lightZ /= lightLen;

        // Compute Lambert diffuse shading
        const diffuse = Math.max(
          0,
          normalX * lightX + normalY * lightY + normalZ * lightZ,
        );
        const ambient = 0.7;
        const intensity = ambient + (1 - ambient) * diffuse;

        // Compute bump mapping offsets (currently zeroed by displacementStrength)
        const offsetX = (nR / 255 - 0.5) * 2;
        const offsetY = (nG / 255 - 0.5) * 2;
        const dx = offsetX * displacementStrength;
        const dy = offsetY * displacementStrength;
        let sampleX = Math.round(x + dx);
        let sampleY = Math.round(y + dy);
        sampleX = Math.max(0, Math.min(width - 1, sampleX));
        sampleY = Math.max(0, Math.min(height - 1, sampleY));
        const sampleIdx = (sampleY * width + sampleX) * 4;

        // Get the original color from the uploaded image
        let r = uploadedData.data[sampleIdx];
        let g = uploadedData.data[sampleIdx + 1];
        let b = uploadedData.data[sampleIdx + 2];
        const a = uploadedData.data[sampleIdx + 3];

        // Convert near-black pixels to dark grey for later blending
        if (r < blackThreshold && g < blackThreshold && b < blackThreshold) {
          r = darkGrey;
          g = darkGrey;
          b = darkGrey;
          isBlackPixel[pixelIndex] = 1;
        }

        compositeData.data[idx] = r * intensity;
        compositeData.data[idx + 1] = g * intensity;
        compositeData.data[idx + 2] = b * intensity;
        compositeData.data[idx + 3] = a;
      }
    }

    // Render the composite image onto the composite canvas
    compCtx.putImageData(compositeData, 0, 0);

    // --- Advanced text/logo extraction ---
    const topLeftAlpha = uploadedData.data[3];
    let textMask;
    if (topLeftAlpha === 0) {
      textMask = new Uint8ClampedArray(width * height);
      for (let i = 0; i < width * height; i++) {
        const idx = i * 4;
        textMask[i] = uploadedData.data[idx + 3] > 0 ? 1 : 0;
      }
    } else {
      const bgR = uploadedData.data[0],
        bgG = uploadedData.data[1],
        bgB = uploadedData.data[2];
      const bgGray = 0.299 * bgR + 0.587 * bgG + 0.114 * bgB;
      const threshold = 40;
      textMask = new Uint8ClampedArray(width * height);
      for (let i = 0; i < width * height; i++) {
        const idx = i * 4;
        const r = uploadedData.data[idx];
        const g = uploadedData.data[idx + 1];
        const b = uploadedData.data[idx + 2];
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
        textMask[i] = Math.abs(gray - bgGray) > threshold ? 1 : 0;
      }
    }

    // Create a separate canvas layer to isolate the text/logo
    const textLayerCanvas = createCanvas(width, height);
    const textLayerCtx = textLayerCanvas.getContext("2d");
    const textLayerData = textLayerCtx.createImageData(width, height);
    for (let i = 0; i < width * height; i++) {
      const idx = i * 4;
      if (textMask[i] === 1) {
        textLayerData.data[idx] = compositeData.data[idx];
        textLayerData.data[idx + 1] = compositeData.data[idx + 1];
        textLayerData.data[idx + 2] = compositeData.data[idx + 2];
        textLayerData.data[idx + 3] = compositeData.data[idx + 3];
      } else {
        textLayerData.data[idx] = 0;
        textLayerData.data[idx + 1] = 0;
        textLayerData.data[idx + 2] = 0;
        textLayerData.data[idx + 3] = 0;
      }
    }

    // Compute a distance transform on the text mask for dynamic fading
    const distanceMap = new Float32Array(width * height);
    const INF = 1e6;
    for (let i = 0; i < width * height; i++) {
      distanceMap[i] = textMask[i] === 1 ? INF : 0;
    }
    // Forward pass
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        if (x > 0) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx - 1] + 1,
          );
        }
        if (y > 0) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx - width] + 1,
          );
        }
        if (x > 0 && y > 0) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx - width - 1] + Math.SQRT2,
          );
        }
        if (x < width - 1 && y > 0) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx - width + 1] + Math.SQRT2,
          );
        }
      }
    }
    // Backward pass
    for (let y = height - 1; y >= 0; y--) {
      for (let x = width - 1; x >= 0; x--) {
        const idx = y * width + x;
        if (x < width - 1) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx + 1] + 1,
          );
        }
        if (y < height - 1) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx + width] + 1,
          );
        }
        if (x < width - 1 && y < height - 1) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx + width + 1] + Math.SQRT2,
          );
        }
        if (x > 0 && y < height - 1) {
          distanceMap[idx] = Math.min(
            distanceMap[idx],
            distanceMap[idx + width - 1] + Math.SQRT2,
          );
        }
      }
    }

    // Compute dynamic fade parameters based on image dimensions
    const baseline = 1500;
    const diagonal = Math.sqrt(width * width + height * height);
    const scaleFactor = diagonal / baseline;
    const fadeDistance = 25 * scaleFactor;
    const darkenMultiplier = 0.5;
    const darkBlack = 25;

    // Apply fade blending to the text layer
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const pixelIndex = y * width + x;
        if (textMask[pixelIndex] === 1) {
          const dt = distanceMap[pixelIndex];
          const normalizedFade = Math.min(1, dt / fadeDistance);
          const curvedFade = Math.sin((normalizedFade * Math.PI) / 2);

          let targetR, targetG, targetB;
          if (isBlackPixel[pixelIndex] === 1) {
            targetR = darkBlack;
            targetG = darkBlack;
            targetB = darkBlack;
          } else {
            targetR = compositeData.data[idx] * darkenMultiplier;
            targetG = compositeData.data[idx + 1] * darkenMultiplier;
            targetB = compositeData.data[idx + 2] * darkenMultiplier;
          }

          textLayerData.data[idx] =
            compositeData.data[idx] * curvedFade + targetR * (1 - curvedFade);
          textLayerData.data[idx + 1] =
            compositeData.data[idx + 1] * curvedFade +
            targetG * (1 - curvedFade);
          textLayerData.data[idx + 2] =
            compositeData.data[idx + 2] * curvedFade +
            targetB * (1 - curvedFade);
          textLayerData.data[idx + 3] = compositeData.data[idx + 3];
        }
      }
    }
    textLayerCtx.putImageData(textLayerData, 0, 0);

    // Apply a subtle shadow and overlay the text layer on the composite
    const shadowOffsetX = width * 0.001;
    const shadowOffsetY = height * 0.001;
    const shadowBlur = Math.max(width, height) * 0.01;

    compCtx.save();
    // (Optional) Set shadow properties if desired:
    // compCtx.shadowColor = "rgba(0, 0, 0, 0.5)";
    // compCtx.shadowBlur = shadowBlur;
    // compCtx.shadowOffsetX = shadowOffsetX;
    // compCtx.shadowOffsetY = -shadowOffsetY;
    compCtx.drawImage(textLayerCanvas, 0, 0);
    compCtx.restore();

    // Overlay the text/logo in its original position
    compCtx.drawImage(textLayerCanvas, 0, 0);

    // Generate the final composite image URL
    const compositeDataURL = compositeCanvas.toDataURL("image/png");

    // Pass the generated URL to the provided handler
    // if (cb) {
    //   cb(compositeDataURL);
    // } else {
    //   handleUrls({ tempUrl: compositeDataURL });
    // }
    offLoader();
    setDimentions(compositeDataURL, cb, isBgRemoved, mainImage);
  };

  // Main function to process an uploaded image using the preloaded texture
  const getTexture = (image, cb = null, isBgRemoved) => {
    if (!image) return;
    onLoader();

    setCustomizationDetails({ ...customizationDetails, isBgRemoved });

    if (!customizationDetails?.isEmbroidery) {
      // handleUrls({ tempUrl: image });
      setDimentions(image, cb, isBgRemoved, image);
      offLoader();
      return;
    }

    const uploadedImg = new Image();
    uploadedImg.crossOrigin = "anonymous";
    uploadedImg.src = image;
    uploadedImg.onload = () => {
      const textureImg = new Image();
      textureImg.crossOrigin = "anonymous";
      textureImg.src = textureSrc; // Ensure textureSrc is defined in your scope
      textureImg.onload = () => {
        processComposite(uploadedImg, textureImg, cb, isBgRemoved, image);
      };
      textureImg.onerror = (err) => {
        console.error("Error loading texture image:", err);
      };
    };
    uploadedImg.onerror = (err) => {
      console.error("Error loading uploaded image:", err);
    };
  };

  const savePreviewImage = debounce(async (step, nextStep) => {
    if (!shirtImgRef.current) return;
    onLoader();

    // Handle the logo-box element
    const logoBoxDiv = shirtImgRef.current.querySelector(".logo-box");

    // Handle all dimentions elements
    const dimentionsDivs = shirtImgRef.current.querySelectorAll(".scale");
    const originalDimensionsVisibility = [];
    dimentionsDivs.forEach((div) => {
      originalDimensionsVisibility.push(div.style.visibility);
    });

    try {
      // Temporarily remove parent's transforms and border-radius
      shirtImgRef.current.style.transform = "none";
      shirtImgRef.current.style.transformOrigin = "0 0";
      shirtImgRef.current.style.borderRadius = "0";

      // Remove the border from the logo-box element
      if (logoBoxDiv) {
        logoBoxDiv.style.border = "none";
      }

      // Hide all dimentions elements by setting their visibility to hidden
      dimentionsDivs.forEach((div) => {
        div.style.visibility = "hidden";
      });

      // Create a high-resolution screenshot
      const canvas = await html2canvas(shirtImgRef.current, {
        scale: 2.5,
        useCORS: true,
        logging: false,
      });

      // const dataUrl = await uploadBaseImage(canvas.toDataURL("image/png"));
      const dataUrl = canvas.toDataURL("image/png");

      // Update the preview URL in partsStep state
      const data = { ...partsStep };
      data[`step${step}`].preview_url = dataUrl;
      setPartsStep(data);
    } catch (error) {
      console.error("Error generating high-quality image:", error);
    } finally {
      offLoader();
    }
  }, 0);
  const handleZoom = (value) => {
    setZoomValue(value);

    const percVal = (value / currentPart?.logo_max_width) * 100;
    const { height, width } = zoom;

    const finalHeight =
      height === 100 ? percVal : (height * (value / 100)).toFixed(0);

    const finalWidth =
      width === 100 ? percVal : (width * (value / 100)).toFixed(0);

    const logo_dimention = { height: finalHeight, width: finalWidth };

    setCurrentPart({
      ...currentPart,
      logo_dimention,
      zoomValue: value,
    });

    const data = { ...partsStep };
    data[`step${step}`].logo_dimention = logo_dimention;
    data[`step${step}`].zoomValue = value;
    setPartsStep(data);
  };

  const updateTextureData = (
    image,
    dimension,
    bgRemovedFlag,
    transparentImg,
    croppedTransparentImg,
  ) => {
    // const dimension =
    //   currentPart?.box_dimension.height === 150 &&
    //   currentPart?.box_dimension.width === 305
    //     ? dimensions?.rectangle
    //     : dimensions?.square;

    const data = {
      ...currentPart,
      texture_logo_url: image,
      logo_dimention: dimension,
      temp_dimention: dimension,
      isBgRemoved: bgRemovedFlag,
      zoomValue: 100,
      showedBgRemovedUrl: transparentImg,
      bgRemovedUrl: croppedTransparentImg,
    };

    setCurrentPart(data);
    setPartsStep({ ...(partsStep || {}), [`step${step}`]: data });
    setZoom(dimension);

    setZoomValue(100);
  };

  // Extract common properties
  const commonProps = {
    isLastStep,
    backLabel: step === 1 ? "Close" : "Back",
    nextLabel: isLastStep ? (
      isSaved ? (
        "Remove customization"
      ) : (
        "Complete customization"
      )
    ) : isLoading ? (
      <div className="d-flex align-items-center gap-2">
        <Spinner style={{ height: 15, width: 15, borderWidth: 1.5 }} />{" "}
        {/* Processing... */}
      </div>
    ) : (
      "Next"
    ),
    nextDisabled:
      isLoading ||
      (deviceStep === 0 &&
        (selected === "device" ||
          selected === "gallery" ||
          partsUploadPreference === "device" ||
          partsUploadPreference === "gallery") &&
        recentUploads?.length === 0),

    className: "d-flex justify-content-end",
    component: isLastStep && (
      <textarea
        disabled={isSaved}
        className="from-control"
        placeholder="Add a note"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
    ),
  };

  // Define a flag for the special case
  const isSpecial =
    ((selected === "device" || selected === "gallery") && step === 3) ||
    partsUploadPreference === "device" ||
    partsUploadPreference === "gallery";

  // Properties for the special case
  const specialProps = {
    onBackClick: () =>
      setDeviceStep((prevState) => {
        if (prevState === 0) {
          if (step === 3) setSelected(null);
          setPartsUploadPreference(null);
          return prevState;
        }
        return prevState - 1;
      }),
    onNextClick: () =>
      setDeviceStep((prevState) => {
        if (deviceStep < 2) {
          if (deviceStep === 1) {
            if (partsUploadPreference) {
              removeBackground(
                currentPart?.cropped_url,
                (transparent, croppedTransparent) => {
                  getTexture(
                    croppedTransparent,
                    (image, dimension) => {
                      updateTextureData(
                        image,
                        dimension,
                        true,
                        transparent,
                        croppedTransparent,
                      );
                    },
                    true,
                  );
                },
              );
            } else {
              removeBackground(urls?.cropped_url);
            }
          }
          let errorObj;
          let valid = true;
          if (prevState === 0) {
            if (
              (step === 3 && !urls?.logo_url) ||
              (step >= 4 && !currentPart?.temp_url)
            ) {
              errorObj = {
                upload:
                  selected === "gallery" || partsUploadPreference === "gallery"
                    ? "Pick an image to proceed."
                    : "Pick or upload an image to proceed.",
                isLabeled: false,
              };
              valid = false;
            }
          }

          if (!valid) {
            setError({ ...errorObj });
            return prevState;
          } else clearError();

          return prevState + 1;
        }
        nextPhaseHandler();
        setPartsUploadPreference(null);
        return prevState;
      }),
  };

  const defaultProps = {
    hideBack: isSaved,
    hideNext: step === 3 && (selected === null || selected === "device"),
    nextStyle: isSaved ? { backgroundColor: "red" } : {},
    onBackClick: () => {
      if (selected === "text" && step === 3) {
        setSelected(null);
      } else if (partsUploadPreference === "text") {
        setPartsUploadPreference(null);
      } else {
        backStep();
      }
    },
    onNextClick: () => {
      setPartsUploadPreference(null);
      nextPhaseHandler();
    },
  };

  const onCart = () => {
    // const container = document.getElementById("json-react-process-data");
    // if (!container) return;

    const payload = {
      isEmbroidery: customizationDetails?.isEmbroidery || false,
      notes: notes || "",
      preview_gallery: [],
    };

    Object.values(partsStep).forEach((part) => {
      const {
        key,
        temp_url,
        logo_url,
        text_url,
        preview_url,
        zoomValue,
        type,
      } = part;
      const [area, position] = key.split("_");

      payload[area] = {
        position: position || area,
        logo_url: text_url || logo_url || temp_url || null,
        preview_url: preview_url || null,
        logo_size: zoomValue || null,
        type,
        ...(type === "text" && {
          text: activeFormats?.[key]?.text || activeFormats?.common?.text,
          font: activeFormats?.[key]?.font || activeFormats?.common?.font,
          color: activeFormats?.[key]?.color || activeFormats?.common?.color,
          font_style:
            activeFormats?.[key]?.textStyles?.font_style ||
            activeFormats?.common?.textStyles?.font_style,
          text_decoration:
            activeFormats?.[key]?.textStyles?.text_decoration ||
            activeFormats?.common?.textStyles?.text_decoration,
          font_weight:
            activeFormats?.[key]?.textStyles?.font_weight ||
            activeFormats?.common?.textStyles?.font_weight,
        }),
      };

      if (preview_url) payload.preview_gallery.push(preview_url);
    });

    // container.value = JSON.stringify(payload);

    localStorage.setItem("customization_payload", JSON.stringify(payload));
    onLabelChange();
    setShow(false);
  };

  const onClose = () => {
    setOpenConfirm(true);
  };

  useEffect(() => {
    if (!customizationDetails?.isEmbroidery) return;

    const textureCanvas = textureCanvasRef.current;
    if (!textureCanvas) return;

    const textureCtx = textureCanvas.getContext("2d");
    const textureImg = new Image();
    textureImg.crossOrigin = "anonymous";
    textureImg.src = textureSrc;
    textureImg.onload = () => {
      // Store the loaded texture image
      textureImgRef.current = textureImg;

      // Match the texture canvas to texture image size
      textureCanvas.width = textureImg.width;
      textureCanvas.height = textureImg.height;

      // Draw the texture onto textureCanvas
      textureCtx.drawImage(textureImg, 0, 0);

      // Grab the pixels
      const textureData = textureCtx.getImageData(
        0,
        0,
        textureCanvas.width,
        textureCanvas.height,
      );

      // Generate the normal map based on texture
      const normalMapData = generateNormalMap(
        textureData,
        textureCanvas.width,
        textureCanvas.height,
      );
      const normalCanvas = normalCanvasRef.current;
      normalCanvas.width = textureCanvas.width;
      normalCanvas.height = textureCanvas.height;
      normalCanvas.getContext("2d").putImageData(normalMapData, 0, 0);
    };
  }, [textureSrc, customizationDetails?.isEmbroidery]);

  useEffect(() => {
    if (partsUploadPreference) setDeviceStep(0);
    else if (
      (selected === "device" || selected === "gallery") &&
      !partsUploadPreference
    )
      setDeviceStep(2);
  }, [partsUploadPreference]);

  useEffect(() => {
    getRecentUploads();
  }, []);

  const Component =
    STEPS_COMPONENT?.[`step${step < 5 ? step : 4}`] || (() => null);

  return (
    <>
      <Modal
        show={show && areas}
        backdrop="static"
        keyboard={false}
        centered
        className="customizationModal"
        // size="xl"
      >
        <Modal.Header>
          <div className="w-100 d-flex justify-content-end">
            <RxCross1
              className="close-btn"
              onClick={() => {
                if (isSaved) setShow(false);
                else onClose();
              }}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="customizer-container">
            {isWrongFormat && (
              <div className="d-flex justify-content-center align-items-center">
                <div id="wrong-json-error">
                  <MdErrorOutline /> There is an error. Please try again later.
                </div>
              </div>
            )}

            <Component
              {...{
                ...urls,
                ...customizationDetails,
                handleCustomizationDetails,
                step,
                partsStep,
                handleUrls,
                setLogoPositions,
                getTexture,
                removeBackground,
                setPartsStep,
                setSelected,
                selected,
                shirtImgRef,
                partsUploadPreference,
                setPartsUploadPreference,
                currentPart,
                setCurrentPart,
                zoomValue,
                setZoomValue,
                zoom,
                setZoom,
                handleZoom,
              }}
            />

            <NavigationButton
              {...(isSpecial
                ? { ...specialProps, ...commonProps }
                : { ...defaultProps, ...commonProps })}
            />
          </div>
          {openConfirm && (
            <ConfirmDialog
              isOpen={openConfirm}
              title="Confirmation"
              footer={
                <>
                  <Button
                    label="Cancel"
                    variant="secondary"
                    size="small"
                    onClick={onReject}
                    style={{
                      padding: "10px 15px",
                      fontSize: 15,
                    }}
                  />
                  <Button
                    label="Confirm"
                    variant="primary"
                    size="small"
                    onClick={onAccept}
                    style={{
                      padding: "10px 15px",
                      fontSize: 15,
                    }}
                  />
                </>
              }
            >
              <div className="text-center">
                Are you sure you want to close the editor?
                <br />
                Any changes or personalizations you’ve made will be lost.
              </div>
            </ConfirmDialog>
          )}
        </Modal.Body>
      </Modal>
      <canvas id="canvas" ref={canvasRef} style={{ display: "none" }} />
      {/* Hidden canvas for normal map */}
      <canvas ref={normalCanvasRef} style={{ display: "none" }} />
      {/* Hidden canvas for the texture */}
      <canvas ref={textureCanvasRef} style={{ display: "none" }} />
    </>
  );
};

export default CustomizationModal;
